/**
 * RecentCollectionsSection Component
 *
 * Displays top 3 collections sorted by most recently updated.
 * Uses Today-specific cards that align with the new warm surface language.
 *
 * Atomic level: Organism
 * Used by: TodayPage
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Database } from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { Card, CardContent } from '../../../ui/card';
import { ConductScoreCircle } from '../../../ui/conduct-score-circle';
import { Skeleton } from '../../../ui/skeleton';
import { useCollections } from '../../../../hooks/useRecords';
import type { Collection } from '../../../../services/types';

const MAX_COLLECTIONS = 3;

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function RecentCollectionCardSkeleton(): React.ReactElement {
  return (
    <Card
      data-testid="recent-collection-skeleton-card"
      className="gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]"
      padding="sm"
    >
      <div className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
        <div className="flex items-end justify-between gap-4 border-t border-border/70 pt-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-14 w-14 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
          <div className="space-y-2 text-right">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
        </div>
      </div>
    </Card>
  );
}

function RecentCollectionCard({
  collection,
  onClick,
}: {
  collection: Collection;
  onClick: () => void;
}): React.ReactElement {
  const hasScore = collection.averageSeverity !== null && collection.averageSeverity !== undefined;
  const displayScore = hasScore ? collection.averageSeverity!.toFixed(1) : 'N/A';

  return (
    <Card
      data-testid={`recent-collection-card-${collection.id}`}
      className="gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 transition-[border-color,box-shadow] duration-200 hover:border-primary/25 hover:shadow-[0_2px_8px_rgba(67,66,245,0.08)]"
      padding="sm"
      clickable
      onClick={onClick}
    >
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-4 px-5 py-4">
          <div className="min-w-0 space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="truncate font-jakarta text-[15px] font-bold tracking-[-0.02em] text-foreground">
                {collection.name}
              </h3>
              <Badge variant="outline" className="rounded-full px-2 py-0 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {collection.type}
              </Badge>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Updated {formatRelativeTime(collection.updatedAt)}
            </p>
          </div>

          <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 px-5 py-4">
          <div className="flex items-center gap-3">
            {hasScore ? (
              <ConductScoreCircle
                score={collection.averageSeverity!}
                size="md"
                showDecimal
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border/70 bg-muted/40">
                <span className="text-[11px] font-semibold text-muted-foreground">N/A</span>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Conduct score
              </div>
              <div className="mt-1 font-jakarta text-xl font-bold tracking-[-0.03em] text-foreground">
                {displayScore}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Database className="h-3.5 w-3.5" />
            <span className="font-semibold text-foreground">
              {(collection.recordCount ?? 0).toLocaleString()}
            </span>
            <span>records</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Displays top 3 most recently updated collections.
 */
export function RecentCollectionsSection(): React.ReactElement {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCollections();

  const recentCollections = useMemo(() => {
    if (!data?.collections) return [];

    return [...data.collections]
      .sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .slice(0, MAX_COLLECTIONS);
  }, [data?.collections]);

  const handleCollectionClick = (collection: Collection) => {
    navigate(`/v4/records/${collection.id}`);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <RecentCollectionCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-border/70 bg-background/70 px-6 py-8 text-center shadow-[var(--shadow-card)]">
        <p className="text-[13px] text-muted-foreground">Unable to load collections</p>
      </div>
    );
  }

  if (recentCollections.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-background/70 px-6 py-10 text-center shadow-[var(--shadow-card)]">
        <p className="text-[13px] font-medium text-foreground">No collections yet</p>
        <p className="mt-2 text-[12px] text-muted-foreground">
          Import records to create your first collection
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {recentCollections.map((collection) => (
        <RecentCollectionCard
          key={collection.id}
          collection={collection}
          onClick={() => handleCollectionClick(collection)}
        />
      ))}
    </div>
  );
}
